import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";

import { useCallback, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { isUndefined } from "lodash";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import { faNewspaper } from "@fortawesome/free-regular-svg-icons/faNewspaper";
import { faComment } from "@fortawesome/free-solid-svg-icons/faComment";
import { faCube } from "@fortawesome/free-solid-svg-icons/faCube";
import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useActivityTracker from "hooks/useActivityTracker";
import highLightText from "utils/highLightText";
import htmlText from "utils/htmlToText";
import shortSecurity from "utils/shortSecurity";

import useArrowKeys from "../useArrowKeys";

type TopNavBarSearchSnippetProps = {
  data: any;
  onClick?: (_event?: React.MouseEvent) => void | Promise<void>;
  onHover?: () => void;
  query?: string;
  subCursor?: number;
  highLighting?: boolean;
  disableNavigation?: boolean;
  itemIndex?: number;
  searchjoySearchId?: number;
  trackSearchFound?: boolean;
};

const entityQuickLinks = [
  {
    name: "DIS",
    href: "/discussions",
    description: "Discussions",
  },
  {
    name: "ANN",
    href: "/announcements",
    description: "Announcements",
  },
  {
    name: "ANA",
    href: "/analytics",
    description: "Analytics",
  },
  {
    name: "THM",
    href: "/themes",
    description: "Themes",
  },
  {
    name: "NWS",
    href: "/news",
    description: "News",
  },
];

const TopNavBarSearchSnippet = ({
  data,
  onClick,
  onHover,
  query,
  subCursor,
  highLighting,
  disableNavigation,
  itemIndex,
  searchjoySearchId,
  trackSearchFound,
}: TopNavBarSearchSnippetProps) => {
  const router = useRouter();

  const { trackActivity } = useActivityTracker();
  const [hovering, setHovering] = useState<string | null>(null);

  const onItemClick = useCallback(
    async (event?: React.MouseEvent) => {
      if (!isUndefined(subCursor) && subCursor >= 0) {
        const link = entityQuickLinks[subCursor];
        if (link) {
          onClick && (await onClick(event));
          const slug = data?.slug || data?.searchable?.slug;
          router.push(`/entities/${slug}${link.href}`);
          return;
        }
      }

      if (data.type === "search-histories") {
        if (data.searchable) {
          onClick && (await onClick(event));
          trackActivity("search.history", data);
          return;
        }

        onClick && (await onClick(event));
        trackActivity("search.history", data);
        router.push(`/search?q=${encodeURIComponent(data.query)}`);
        return;
      }

      const parameters = {
        "item-position": (itemIndex || 0) + 1,
        page: 1,
        query,
        "searchjoy-search-id": searchjoySearchId,
      };
      onClick && (await onClick(event));
      if (trackSearchFound) trackActivity("search.found", data, parameters);

      if (disableNavigation) return;

      if (data.type === "insights") {
        router.push(`/insights/${data.slug}`);
        return;
      }

      if (data.type === "entities") {
        router.push(`/entities/${data.slug}`);
        return;
      }

      if (data.type === "accounts" || data.type === "insight-providers") {
        router.push(`/profiles/${data.slug}`);
        return;
      }

      if (data.type === "companies") {
        router.push(`/companies/${data.slug}`);
        return;
      }

      if (data.type === "comments") {
        router.push(`/discussions?id=${data.id}`);
        return;
      }

      if (data.type === "collections") {
        router.push(`/collections/${data.slug}`);
        return;
      }
    },
    [
      data,
      disableNavigation,
      itemIndex,
      onClick,
      query,
      router,
      searchjoySearchId,
      subCursor,
      trackActivity,
      trackSearchFound,
    ]
  );

  useArrowKeys({
    onEnter(event) {
      if (highLighting) {
        event.preventDefault();
        onItemClick();
      }
    },
  });

  const className = twMerge(
    // "hover:bg-smart-teal hover:text-white",
    highLighting ? "bg-smart-teal text-white" : "",
    "group"
  );

  const subClassName = twMerge(
    // "group-hover:text-white",
    highLighting && "text-white"
  );

  const entityQuickLink = useMemo(() => {
    return entityQuickLinks.map((link, index) => (
      <OverlayTrigger
        key={index}
        show={(highLighting && subCursor === index) || hovering === link.name}
        overlay={
          <Tooltip show>
            <div className="">{link.description}</div>
          </Tooltip>
        }
        trigger={["hover", "focus"]}
      >
        <Link
          href={`/entities/${data.slug}${link.href}`}
          className={twMerge(
            "text-[9px] border border-solid p-[2px] px-[5px] rounded hover:bg-jade hover:!border-jade hover:underline hover:!text-white",
            highLighting
              ? subCursor === index
                ? "bg-jade !text-white !border-jade underline"
                : "!text-jade !border-white group-hover:text-white bg-white"
              : "text-jade border-jade"
          )}
          onClick={async (event) => {
            event.preventDefault();
            onClick && (await onClick(event));
            router.push(`/entities/${data.slug}${link.href}`);
          }}
          onMouseEnter={() => {
            if (highLighting) {
              setHovering(link.name);
            }
          }}
          onMouseLeave={() => setHovering(null)}
        >
          {link.name}
        </Link>
      </OverlayTrigger>
    ));
  }, [data.slug, highLighting, hovering, onClick, router, subCursor]);

  if (data.type === "search-histories") {
    if (data.searchable) {
      return (
        <TopNavBarSearchSnippet
          data={data.searchable}
          onClick={onItemClick}
          onHover={onHover}
          query={query}
          subCursor={subCursor}
          highLighting={highLighting}
        />
      );
    }

    return (
      <div
        className="w-full relative"
        onClick={onItemClick}
        onMouseOver={onHover}
      >
        <div
          className={twMerge(
            className,
            "truncate",
            "px-[7px] py-[3px] cursor-pointer"
          )}
        >
          <FontAwesomeIcon
            className={twMerge("mr-[5px] text-[#ccc]", subClassName)}
            icon={faSearch}
          />
          <span className="text-[15px]">{data.query}</span>
        </div>
      </div>
    );
  }

  if (data.type === "insights") {
    const text = data.tagline;
    const highLightedText = query ? highLightText(text, query) : text;

    const tags = [];
    const entity = data.primaryEntity || data.entities?.[0];
    if (entity?.prettyName) {
      tags.push(entity?.prettyName);
    } else if (!!data.countries?.length) {
      tags.push(...data.countries);
    }

    if (!!data.contentVerticals?.length) {
      tags.push(...data.contentVerticals?.map?.((cv: any) => cv.name));
    }

    if (data.contentVertical) {
      tags.push(data.contentVertical.name);
    }
    const tagsText = tags.filter((d) => d).join(" / ");
    const highLightedTagsText = query
      ? highLightText(tagsText, query)
      : tagsText;

    return (
      <div
        className="w-full relative"
        onClick={onItemClick}
        onMouseOver={onHover}
      >
        <div
          className={twMerge(
            className,
            "truncate",
            "px-[7px] py-[3px] cursor-pointer",
            "group"
          )}
        >
          <FontAwesomeIcon
            className={twMerge("mr-[5px] text-[#ccc]", subClassName)}
            icon={faNewspaper}
          />
          <span className="text-[15px]">{highLightedText}</span>
          <span
            className={twMerge(
              "text-[#999]",
              "ml-[10px] text-[13px]",
              subClassName
            )}
          >
            {highLightedTagsText}
          </span>
        </div>
      </div>
    );
  }

  if (data.type === "entities") {
    const text = `${data.prettyName} ${
      data?.security ? ` (${shortSecurity(data?.security)})` : ""
    }`;
    const highLightedText = query ? highLightText(text, query) : text;

    const tags = [data.country, data.sector];
    const tagsText = tags.filter((d) => d).join(", ");
    const highLightedTagsText = query
      ? highLightText(tagsText, query)
      : tagsText;

    return (
      <div
        className="flex justify-between items-center w-full relative"
        onMouseOver={onHover}
      >
        <div
          className={twMerge(
            className,
            "truncate w-full",
            "px-[7px] py-[3px] cursor-pointer",
            "flex items-center justify-between group"
          )}
        >
          <div
            className={twMerge("flex items-center flex-1")}
            onClick={onItemClick}
          >
            {data.logoUrl?.small ? (
              <span className="mr-[5px]">
                <div className="w-[18px] h-[18px] border border-solid border-black/20 bg-white">
                  <Image
                    className="align-top"
                    src={data.logoUrl?.small}
                    alt={data.shortName}
                    width={16}
                    height={16}
                  />
                </div>
              </span>
            ) : (
              <FontAwesomeIcon
                className={twMerge("mr-[5px] text-[#ccc]", subClassName)}
                icon={faCube}
              />
            )}
            <span className="text-[15px]">{highLightedText}</span>
            <span
              className={twMerge(
                "ml-[10px] text-[12px] text-[#999] ",
                subClassName
              )}
            >
              {highLightedTagsText}
            </span>
          </div>
          <div className="flex items-center gap-[10px]">{entityQuickLink}</div>
        </div>
      </div>
    );
  }

  if (data.type === "accounts" || data.type === "insight-providers") {
    const text = data.name;
    const highLightedText = query ? highLightText(text, query) : text;

    const tags = [data.description];
    const tagsText = tags.filter((d) => d).join(", ");
    const highLightedTagsText = query
      ? highLightText(tagsText, query)
      : tagsText;

    return (
      <div
        className="w-full relative"
        onClick={onItemClick}
        onMouseOver={onHover}
      >
        <div
          className={twMerge(
            className,
            "truncate",
            "px-[7px] py-[3px] cursor-pointer",
            "group flex items-center"
          )}
        >
          {data.avatarUrl?.small ? (
            <span className="mr-[5px]">
              <div className="w-[18px] h-[18px] border border-solid border-black/20 rounded-full bg-white">
                <Image
                  className="rounded-full align-top"
                  src={data.avatarUrl?.small}
                  alt={data.name}
                  width={16}
                  height={16}
                />
              </div>
            </span>
          ) : (
            <FontAwesomeIcon
              className={twMerge("mr-[5px] text-[#ccc]", subClassName)}
              icon={faNewspaper}
            />
          )}
          <span className="text-[15px]">{highLightedText}</span>
          <span
            className={twMerge(
              "ml-[10px] text-[12px] text-[#999] group-hover:text-white",
              subClassName
            )}
          >
            {highLightedTagsText}
          </span>
        </div>
      </div>
    );
  }

  if (data.type === "companies") {
    const text = data.name;
    const highLightedText = query ? highLightText(text, query) : text;

    const tags = [data.description];
    const tagsText = tags.filter((d) => d).join(", ");
    const highLightedTagsText = query
      ? highLightText(tagsText, query)
      : tagsText;

    return (
      <div
        className="w-full relative"
        onClick={onItemClick}
        onMouseOver={onHover}
      >
        <div
          className={twMerge(
            className,
            "truncate",
            "px-[7px] py-[3px] cursor-pointer",
            "group flex items-center"
          )}
        >
          {data.avatarUrl?.small ? (
            <span className="mr-[5px]">
              <div className="w-[18px] h-[18px] border border-solid border-black/20 rounded-full bg-white">
                <Image
                  className="rounded-full align-top"
                  src={data.avatarUrl?.small}
                  alt={data.name}
                  width={16}
                  height={16}
                />
              </div>
            </span>
          ) : (
            <FontAwesomeIcon
              className={twMerge("mr-[5px] text-[#ccc]", subClassName)}
              icon={faNewspaper}
            />
          )}
          <span className="text-[15px]">{highLightedText}</span>
          <span
            className={twMerge(
              "ml-[10px] text-[12px] text-[#999] ",
              subClassName
            )}
          >
            {highLightedTagsText}
          </span>
        </div>
      </div>
    );
  }

  if (data.type === "comments") {
    const text = data.commenter?.name;
    const highLightedText = query ? highLightText(text, query) : text;

    const subText = htmlText(data.body);
    const highLightedSubText = query ? highLightText(subText, query) : subText;

    return (
      <div
        className="w-full relative"
        onClick={onItemClick}
        onMouseOver={onHover}
      >
        <div
          className={twMerge(
            className,
            "truncate",
            "px-[7px] py-[3px] cursor-pointer",
            "group"
          )}
        >
          <FontAwesomeIcon
            className={twMerge("mr-[5px] text-[#ccc]", subClassName)}
            icon={faComment}
          />
          <span className="text-[15px]">{highLightedText}: </span>
          <span className="text-[15px]">{highLightedSubText}</span>
        </div>
      </div>
    );
  }

  if (data.type === "collections") {
    const text = data.name;
    const highLightedText = query ? highLightText(text, query) : text;

    const subText = data.description;
    const highLightedSubText = query ? highLightText(subText, query) : subText;

    return (
      <div
        className="w-full relative"
        onClick={onItemClick}
        onMouseOver={onHover}
      >
        <div
          className={twMerge(
            className,
            "truncate",
            "px-[7px] py-[3px] cursor-pointer",
            "group flex items-center"
          )}
        >
          {data.coverImageUrl?.small ? (
            <span className="mr-[5px]">
              <div className="w-[18px] h-[18px] border border-solid border-black/20 bg-white">
                <Image
                  className="align-top"
                  src={data.coverImageUrl?.small}
                  alt={data.name}
                  width={16}
                  height={16}
                />
              </div>
            </span>
          ) : (
            <FontAwesomeIcon
              className={twMerge("mr-[5px] text-[#ccc]", subClassName)}
              icon={faNewspaper}
            />
          )}
          <span className="text-[15px]">{highLightedText}</span>
          <span
            className={twMerge(
              "ml-[10px] text-[12px] text-[#999] ",
              subClassName
            )}
          >
            Collection {(data.totalInsights || highLightedSubText) && "—"}{" "}
            {highLightedSubText}{" "}
            {data.totalInsights
              ? `(${numeral(data.totalInsights).format("0,0")} insights)`
              : ""}
          </span>
        </div>
      </div>
    );
  }

  return <></>;
};

export default TopNavBarSearchSnippet;
